// ── v1.1 launch flags in the SPA ─────────────────────────────────────────────
// GET /api/public/config tells the SPA which launch features are on (all false by default).
// If the call fails (older API, network), every flag is OFF and the app behaves as r2b.
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface LaunchFlags {
  legalPages: boolean; signupConsent: boolean; dataExport: boolean; honestCopy: boolean;
  firstRun: boolean; reliability: boolean; productAnalytics: boolean; supportLink: boolean;
  seoMeta: boolean; signupBotProtection: boolean;
}
export interface LaunchConfig { flags: LaunchFlags; supportEmail: string | null; legalVersion: string | null }

export const LAUNCH_OFF: LaunchConfig = {
  flags: {
    legalPages: false, signupConsent: false, dataExport: false, honestCopy: false, firstRun: false,
    reliability: false, productAnalytics: false, supportLink: false, seoMeta: false, signupBotProtection: false,
  },
  supportEmail: null, legalVersion: null,
};

/** Only an explicit `true` from the server turns a flag on. */
export function normalizeLaunchConfig(raw: unknown): LaunchConfig {
  const r = (raw ?? {}) as { flags?: Record<string, unknown>; supportEmail?: unknown; legalVersion?: unknown };
  const flags = { ...LAUNCH_OFF.flags };
  for (const k of Object.keys(flags) as (keyof LaunchFlags)[]) flags[k] = r.flags?.[k] === true;
  return {
    flags,
    supportEmail: flags.supportLink && typeof r.supportEmail === "string" ? r.supportEmail : null,
    legalVersion: typeof r.legalVersion === "string" ? r.legalVersion : null,
  };
}

let current: LaunchConfig = LAUNCH_OFF;
/** Last config the hook loaded (for non-React helpers such as analytics). */
export function currentLaunchConfig(): LaunchConfig { return current; }

export function useLaunchConfig(): LaunchConfig & { ready: boolean } {
  const q = useQuery({
    queryKey: ["public-config"],
    queryFn: async () => {
      try { return normalizeLaunchConfig((await axios.get("/api/public/config")).data); }
      catch { return LAUNCH_OFF; }
    },
    staleTime: 5 * 60_000,
    retry: false,        // a failure resolves to all-OFF (never throws); Login re-fetches if the server disagrees
  });
  current = q.data ?? LAUNCH_OFF;
  return { ...current, ready: q.isFetched || q.isError };
}
